import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Segmented,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  theme as antdTheme,
} from 'antd';
import {
  ArrowLeftOutlined,
  BookOutlined,
  DeleteOutlined,
  HeartOutlined,
  HomeOutlined,
  IdcardOutlined,
  PercentageOutlined,
  PhoneOutlined,
  PlusOutlined,
  SaveOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import dayjs, { type Dayjs } from 'dayjs';
import { feeApi } from '@/api/fee';
import { frontDeskApi } from '@/api/frontdesk';
import { setupApi } from '@/api/setup';
import { studentsApi, type RegisterStudent } from '@/api/students';
import { FormActionBar } from '@/shared/components/form-action-bar';
import { PageHeader } from '@/shared/components/page-header';
import { PhotoUpload } from '@/shared/components/photo-upload';
import { extractError } from '@/shared/lib/error-message';
import { usePhotoUpload } from '@/shared/lib/use-photo-upload';
import { useStudentLookups } from './lookups';

const FIVE_MIN = 5 * 60 * 1000;

interface DiscountRow {
  feeTypeId?: number;
  discountTypeId?: number;
  percentage?: boolean;
  value?: number;
  remarks?: string;
}

interface FormShape {
  firstName: string;
  lastName: string;
  dateOfBirth?: Dayjs;
  genderId?: number;
  photoPath?: string | null;
  bloodGroupId?: number;
  religionId?: number;
  nationalityId?: number;
  addressLine?: string;
  cityId?: number;

  guardianRelationshipTypeId: number;
  guardianFullName: string;
  guardianCnic?: string;
  guardianPhone?: string;
  guardianOccupation?: string;

  classSectionId: number;
  enrollmentDate: Dayjs;

  discounts?: DiscountRow[];

  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyRelation?: string;
  medicalNotes?: string;
  prevSchoolName?: string;
  prevSchoolLeavingClass?: string;
  prevSchoolTcNumber?: string;
}

export function NewStudentPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm<FormShape>();
  const lookups = useStudentLookups();
  const photo = usePhotoUpload('student-photos');
  const [searchParams] = useSearchParams();
  const fromInquiry = searchParams.get('fromInquiry');
  const fromInquiryId = fromInquiry ? Number(fromInquiry) : null;

  const inquiryQuery = useQuery({
    queryKey: ['frontdesk', 'inquiry', fromInquiryId],
    queryFn: () => frontDeskApi.getInquiry(fromInquiryId!),
    enabled: fromInquiryId != null && Number.isFinite(fromInquiryId),
  });

  const currentYear =
    lookups.years.find((y) => y.current) ?? lookups.years[0] ?? null;

  const extras = useQueries({
    queries: [
      {
        queryKey: ['setup', 'class-sections', currentYear?.id ?? 'none'],
        queryFn: () => setupApi.listClassSections(currentYear?.id),
        enabled: currentYear != null,
        staleTime: FIVE_MIN,
      },
      {
        queryKey: ['fee', 'types', 'active'],
        queryFn: () => feeApi.listTypes(true),
        staleTime: FIVE_MIN,
      },
      {
        queryKey: ['setup', 'lookups', 'discount-types', 'active'],
        queryFn: () => setupApi.listLookup('discount-types', true),
        staleTime: FIVE_MIN,
      },
    ],
  });
  const [classSectionsQ, feeTypesQ, discountTypesQ] = extras;
  const classSections = classSectionsQ.data ?? [];
  const feeTypes = feeTypesQ.data ?? [];
  const discountTypes = discountTypesQ.data ?? [];

  // Discounts may only target fees actually assigned to the chosen class·section —
  // the backend rejects anything else, which would fail the whole atomic registration.
  const watchedClassSectionId = Form.useWatch('classSectionId', form);
  const sectionStructuresQ = useQuery({
    queryKey: ['fee', 'structures', currentYear?.id ?? 'none', watchedClassSectionId ?? 'none'],
    queryFn: () => feeApi.listStructures(currentYear!.id, watchedClassSectionId!),
    enabled: currentYear != null && watchedClassSectionId != null,
    staleTime: FIVE_MIN,
  });
  const assignedFeeTypes = (sectionStructuresQ.data ?? []).map((s) => ({
    id: s.feeTypeId,
    name: s.feeTypeName ?? `#${s.feeTypeId}`,
  }));

  // Prefill form from inquiry once both the inquiry and the class·section list have loaded.
  useEffect(() => {
    const inq = inquiryQuery.data;
    if (!inq) return;
    const [first, ...rest] = inq.studentName.trim().split(/\s+/);
    const last = rest.join(' ');
    const matchedSection = inq.classInterestedId
      ? classSections.find((cs) => cs.classId === inq.classInterestedId)
      : undefined;
    form.setFieldsValue({
      firstName: first ?? '',
      lastName: last || first || '',
      guardianFullName: inq.guardianName ?? '',
      guardianPhone: inq.guardianPhone ?? '',
      classSectionId: matchedSection?.id,
    });
  }, [inquiryQuery.data, classSections, form]);

  const register = useMutation({
    mutationFn: (body: RegisterStudent) => studentsApi.register(body),
    onSuccess: async (created) => {
      message.success(
        `Registered ${created.firstName} ${created.lastName} (${created.registrationNumber})`,
      );
      if (fromInquiryId != null) {
        try {
          await frontDeskApi.convertInquiry(fromInquiryId, { studentId: created.id });
        } catch (e) {
          message.warning(
            `Registered, but failed to mark inquiry as converted: ${extractError(e, 'unknown error')}`,
          );
        }
      }
      navigate(`/students/${created.id}`, { replace: true });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const onFinish = async (values: FormShape) => {
    if (!currentYear) {
      message.error('No active academic year configured. Set one in Setup first.');
      return;
    }
    let photoPath = values.photoPath ?? null;
    try {
      const uploaded = await photo.commit();
      if (uploaded !== undefined) photoPath = uploaded;
    } catch (e) {
      message.error(extractError(e, 'Photo upload failed'));
      return;
    }

    const discounts = (values.discounts ?? [])
      .filter((d) => d.feeTypeId != null && d.discountTypeId != null && d.value != null)
      .map((d) => ({
        feeTypeId: d.feeTypeId!,
        discountTypeId: d.discountTypeId!,
        percentage: d.percentage ?? false,
        value: String(d.value),
        remarks: d.remarks?.trim() || null,
      }));

    const body: RegisterStudent = {
      student: {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.format('YYYY-MM-DD') : null,
        genderId: values.genderId ?? null,
        photoPath,
        bloodGroupId: values.bloodGroupId ?? null,
        religionId: values.religionId ?? null,
        nationalityId: values.nationalityId ?? null,
        addressLine: values.addressLine?.trim() || null,
        cityId: values.cityId ?? null,
        emergencyContactName: values.emergencyContactName?.trim() || null,
        emergencyContactPhone: values.emergencyContactPhone?.trim() || null,
        emergencyRelation: values.emergencyRelation?.trim() || null,
        medicalNotes: values.medicalNotes?.trim() || null,
        prevSchoolName: values.prevSchoolName?.trim() || null,
        prevSchoolLeavingClass: values.prevSchoolLeavingClass?.trim() || null,
        prevSchoolTcNumber: values.prevSchoolTcNumber?.trim() || null,
      },
      primaryGuardian: {
        relationshipTypeId: values.guardianRelationshipTypeId,
        fullName: values.guardianFullName.trim(),
        cnic: values.guardianCnic?.trim() || null,
        phone: values.guardianPhone?.trim() || null,
        occupation: values.guardianOccupation?.trim() || null,
        primaryContact: true,
      },
      enrollment: {
        academicYearId: currentYear.id,
        classSectionId: values.classSectionId,
        enrollmentDate: values.enrollmentDate.format('YYYY-MM-DD'),
        inquiryId: fromInquiryId ?? undefined,
      },
      discounts: discounts.length > 0 ? discounts : undefined,
      inquiryId: fromInquiryId ?? undefined,
    };
    register.mutate(body);
  };

  const opt = (rows: { id: number; name: string }[]) =>
    rows.map((r) => ({ value: r.id, label: r.name }));
  const cityOpt = (rows: { id: number; name: string; area: string | null }[]) =>
    rows.map((r) => ({ value: r.id, label: r.area ? `${r.name} · ${r.area}` : r.name }));
  const classSectionOpt = classSections
    .slice()
    .sort((a, b) => a.classSortOrder - b.classSortOrder || a.sectionName.localeCompare(b.sectionName))
    .map((cs) => ({
      value: cs.id,
      label: `${cs.className} · ${cs.sectionName}`,
    }));

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow={fromInquiryId != null ? 'Module / Front Desk → Students' : 'Module / Students'}
        title={fromInquiryId != null ? 'Convert inquiry to enrolled student' : 'Register a new student'}
        description={
          fromInquiryId != null && inquiryQuery.data
            ? `From inquiry · ${inquiryQuery.data.studentName}${inquiryQuery.data.classInterestedName ? ` · interested in ${inquiryQuery.data.classInterestedName}` : ''}. Review &amp; complete the form, then save.`
            : currentYear
              ? `Admission for academic year ${currentYear.name}. Registration number is auto-assigned on save.`
              : 'No active academic year — configure one in Setup before registering students.'
        }
        actions={
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(fromInquiryId != null ? '/frontdesk/inquiries' : '/students')}>
            Back
          </Button>
        }
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        disabled={register.isPending || lookups.isLoading}
        initialValues={{
          enrollmentDate: dayjs(),
          discounts: [],
        }}
      >
        <Row gutter={[20, 20]}>
          <Col xs={24} xl={16}>
            <Space orientation="vertical" size={14} style={{ width: '100%' }}>
              <SectionCard icon={<IdcardOutlined />} title="Identity">
                <Row gutter={[14, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="First name"
                      name="firstName"
                      rules={[{ required: true, max: 100 }]}
                    >
                      <Input autoFocus placeholder="e.g. Ahmed" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Last name" name="lastName" rules={[{ required: true, max: 100 }]}>
                      <Input placeholder="e.g. Khan" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="Date of birth" name="dateOfBirth">
                      <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" disabledDate={(d) => d && d.isAfter(dayjs())} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={8}>
                    <Form.Item label="Gender" name="genderId">
                      <Select allowClear options={opt(lookups.genders)} placeholder="—" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={8}>
                    <Form.Item label="Blood group" name="bloodGroupId">
                      <Select allowClear options={opt(lookups.bloodGroups)} placeholder="—" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={8}>
                    <Form.Item label="Religion" name="religionId">
                      <Select allowClear options={opt(lookups.religions)} placeholder="—" />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={8}>
                    <Form.Item label="Nationality" name="nationalityId">
                      <Select allowClear options={opt(lookups.nationalities)} placeholder="—" />
                    </Form.Item>
                  </Col>
                </Row>
              </SectionCard>

              <SectionCard icon={<HomeOutlined />} title="Address">
                <Row gutter={[14, 0]}>
                  <Col xs={24} md={16}>
                    <Form.Item label="Address line" name="addressLine">
                      <Input placeholder="House #, Street, Locality" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="City" name="cityId">
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        options={cityOpt(lookups.cities)}
                        placeholder="—"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </SectionCard>

              <SectionCard
                icon={<TeamOutlined />}
                title="Primary guardian"
                hint="Required — used for fee notices &amp; pickups"
              >
                <Row gutter={[14, 0]}>
                  <Col xs={24} md={6}>
                    <Form.Item
                      label="Relation"
                      name="guardianRelationshipTypeId"
                      rules={[{ required: true, message: 'Select relation' }]}
                    >
                      <Select options={opt(lookups.relations)} placeholder="—" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={10}>
                    <Form.Item
                      label="Full name"
                      name="guardianFullName"
                      rules={[{ required: true, max: 200 }]}
                    >
                      <Input placeholder="e.g. Muhammad Aslam" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="CNIC" name="guardianCnic" rules={[{ max: 15 }]}>
                      <Input placeholder="35202-xxxxxxx-x" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="Phone" name="guardianPhone" rules={[{ max: 20 }]}>
                      <Input prefix={<PhoneOutlined style={{ color: 'var(--ant-color-text-tertiary)' }} />} placeholder="03xx-xxxxxxx" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={16}>
                    <Form.Item label="Occupation" name="guardianOccupation" rules={[{ max: 100 }]}>
                      <Input placeholder="e.g. Shopkeeper" />
                    </Form.Item>
                  </Col>
                </Row>
              </SectionCard>

              <SectionCard
                icon={<BookOutlined />}
                title="Enrollment"
                hint={currentYear ? `Year ${currentYear.name}` : 'No active year'}
              >
                <Row gutter={[14, 0]}>
                  <Col xs={24} md={14}>
                    <Form.Item
                      label="Class · Section"
                      name="classSectionId"
                      rules={[{ required: true, message: 'Select class &amp; section' }]}
                    >
                      <Select
                        showSearch
                        optionFilterProp="label"
                        options={classSectionOpt}
                        placeholder={classSectionOpt.length === 0 ? 'No class·sections defined yet' : '—'}
                        disabled={classSectionOpt.length === 0}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={10}>
                    <Form.Item
                      label="Enrollment date"
                      name="enrollmentDate"
                      rules={[{ required: true, message: 'Pick a date' }]}
                    >
                      <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                    </Form.Item>
                  </Col>
                </Row>
              </SectionCard>

              <OptionalDetails
                feeTypes={assignedFeeTypes}
                sectionSelected={watchedClassSectionId != null}
                discountTypes={discountTypes}
                opt={opt}
              />
            </Space>
          </Col>

          <Col xs={24} xl={8}>
            <PreviewSidebar
              form={form}
              currentYearName={currentYear?.name ?? null}
              academicYearId={currentYear?.id ?? null}
              photo={photo}
              feeTypes={feeTypes}
            />
          </Col>
        </Row>
      </Form>

      <FormActionBar
        hint="Saving creates the student, attaches the guardian, and enrolls in one step."
        secondary={
          <Button onClick={() => navigate('/students')} disabled={register.isPending}>
            Cancel
          </Button>
        }
        primary={
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={register.isPending}
            onClick={() => form.submit()}
            disabled={!currentYear}
          >
            Save &amp; enroll
          </Button>
        }
      />
    </div>
  );
}

/* ---------- Section primitives ---------- */

export function SectionCard({
  icon,
  title,
  hint,
  children,
  bordered = true,
}: {
  icon: ReactNode;
  title: string;
  hint?: ReactNode;
  children: ReactNode;
  bordered?: boolean;
}) {
  return (
    <Card
      variant={bordered ? 'outlined' : 'borderless'}
      styles={{
        header: { minHeight: 44, padding: '0 18px' },
        body: { padding: '16px 18px 4px' },
      }}
      title={
        <Space size={8} style={{ fontSize: 14 }}>
          <span style={{ color: 'var(--ant-color-primary)', fontSize: 16, display: 'inline-flex' }}>{icon}</span>
          <Typography.Text strong style={{ fontSize: 14 }}>{title}</Typography.Text>
        </Space>
      }
      extra={
        hint ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {hint}
          </Typography.Text>
        ) : undefined
      }
    >
      {children}
    </Card>
  );
}

/* ---------- Optional details (discounts + emergency + medical + previous school) ---------- */

function OptionalDetails({
  feeTypes,
  sectionSelected,
  discountTypes,
  opt,
}: {
  feeTypes: { id: number; name: string }[];
  sectionSelected: boolean;
  discountTypes: { id: number; name: string }[];
  opt: (rows: { id: number; name: string }[]) => { value: number; label: string }[];
}) {
  const { token } = antdTheme.useToken();
  return (
    <Collapse
      ghost={false}
      bordered
      style={{ background: token.colorBgContainer }}
      items={[
        {
          key: 'discounts',
          label: (
            <Space size={8}>
              <PercentageOutlined style={{ color: 'var(--ant-color-primary)' }} />
              <Typography.Text strong style={{ fontSize: 14 }}>Fee discounts</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Optional · sibling, staff-child, merit
              </Typography.Text>
            </Space>
          ),
          children: (
            <Form.List name="discounts">
              {(fields, { add, remove }) => (
                <Space orientation="vertical" size={12} style={{ width: '100%' }}>
                  {fields.length === 0 && (
                    <Typography.Text type="secondary">
                      {!sectionSelected
                        ? 'Pick a class · section first — discounts apply to the fees assigned to that class.'
                        : feeTypes.length === 0
                          ? 'No fees are assigned to this class yet — set up its fee structure under Fee → Fee structures.'
                          : 'No discounts. Click "Add discount" to apply one or more concessions.'}
                    </Typography.Text>
                  )}

                  {fields.map((field) => (
                    <Row key={field.key} gutter={[12, 0]} align="middle">
                      <Col xs={24} md={6}>
                        <Form.Item
                          label="Fee type"
                          name={[field.name, 'feeTypeId']}
                          rules={[{ required: true, message: 'Required' }]}
                          style={{ marginBottom: 8 }}
                        >
                          <Select options={feeTypes.map((f) => ({ value: f.id, label: f.name }))} placeholder="—" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={5}>
                        <Form.Item
                          label="Concession"
                          name={[field.name, 'discountTypeId']}
                          rules={[{ required: true, message: 'Required' }]}
                          style={{ marginBottom: 8 }}
                        >
                          <Select options={opt(discountTypes)} placeholder="—" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={4}>
                        <Form.Item
                          label="Mode"
                          name={[field.name, 'percentage']}
                          valuePropName="value"
                          initialValue={false}
                          style={{ marginBottom: 8 }}
                        >
                          <Segmented<boolean>
                            block
                            options={[
                              { label: '%', value: true },
                              { label: 'Rs', value: false },
                            ]}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={5}>
                        <Form.Item
                          label="Value"
                          name={[field.name, 'value']}
                          rules={[{ required: true, message: 'Required' }, { type: 'number', min: 0.01 }]}
                          style={{ marginBottom: 8 }}
                        >
                          <InputNumber style={{ width: '100%' }} min={0.01} step={1} placeholder="0" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={3}>
                        <Form.Item label=" " style={{ marginBottom: 8 }}>
                          <Tooltip title="Remove discount">
                            <Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} block />
                          </Tooltip>
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item name={[field.name, 'remarks']} style={{ marginBottom: 0 }}>
                          <Input placeholder="Remarks (optional)" maxLength={1000} />
                        </Form.Item>
                      </Col>
                    </Row>
                  ))}

                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => add({ percentage: false })}
                    block
                    disabled={feeTypes.length === 0 || discountTypes.length === 0}
                  >
                    Add discount
                  </Button>
                </Space>
              )}
            </Form.List>
          ),
        },
        {
          key: 'emergency',
          label: (
            <Space size={8}>
              <PhoneOutlined style={{ color: 'var(--ant-color-primary)' }} />
              <Typography.Text strong style={{ fontSize: 14 }}>Emergency contact</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Optional</Typography.Text>
            </Space>
          ),
          children: (
            <Row gutter={[14, 0]}>
              <Col xs={24} md={10}>
                <Form.Item label="Contact name" name="emergencyContactName">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Phone" name="emergencyContactPhone">
                  <Input placeholder="03xx-xxxxxxx" />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Relation" name="emergencyRelation">
                  <Input placeholder="e.g. Uncle" />
                </Form.Item>
              </Col>
            </Row>
          ),
        },
        {
          key: 'medical',
          label: (
            <Space size={8}>
              <HeartOutlined style={{ color: 'var(--ant-color-primary)' }} />
              <Typography.Text strong style={{ fontSize: 14 }}>Medical &amp; previous school</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Optional</Typography.Text>
            </Space>
          ),
          children: (
            <Row gutter={[14, 0]}>
              <Col xs={24}>
                <Form.Item
                  label="Medical notes"
                  name="medicalNotes"
                  tooltip="Allergies, conditions, recurring medication."
                >
                  <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={10}>
                <Form.Item label="Previous school" name="prevSchoolName">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={7}>
                <Form.Item label="Leaving class" name="prevSchoolLeavingClass">
                  <Input placeholder="e.g. Grade 4" />
                </Form.Item>
              </Col>
              <Col xs={24} md={7}>
                <Form.Item label="TC number" name="prevSchoolTcNumber">
                  <Input placeholder="Transfer cert. number" />
                </Form.Item>
              </Col>
            </Row>
          ),
        },
      ]}
    />
  );
}

/* ---------- Right-rail preview ---------- */

function PreviewSidebar({
  form,
  currentYearName,
  academicYearId,
  photo,
  feeTypes,
}: {
  form: ReturnType<typeof Form.useForm<FormShape>>[0];
  currentYearName: string | null;
  academicYearId: number | null;
  photo: ReturnType<typeof usePhotoUpload>;
  feeTypes: { id: number; name: string }[];
}) {
  const firstName = Form.useWatch('firstName', form);
  const lastName = Form.useWatch('lastName', form);
  const classSectionId = Form.useWatch('classSectionId', form);
  const discounts = Form.useWatch('discounts', form) ?? [];
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  return (
    <div style={{ position: 'sticky', top: 16, display: 'grid', gap: 14 }}>
      <SectionCard icon={<UserOutlined />} title="Photo &amp; preview">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, paddingBlock: 4 }}>
          <Form.Item name="photoPath" style={{ marginBottom: 0 }}>
            <PhotoUpload
              category="student-photos"
              onPendingChange={photo.onPendingChange}
              size={108}
            />
          </Form.Item>
          <div style={{ textAlign: 'center', minHeight: 38 }}>
            <Typography.Text strong style={{ fontSize: 16, display: 'block' }}>
              {fullName || <span style={{ color: 'var(--ant-color-text-quaternary)' }}>Student name</span>}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {currentYearName ? `Academic year ${currentYearName}` : 'No active year'}
            </Typography.Text>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={<BookOutlined />} title="Admission bill preview">
        <AdmissionBillPreview
          academicYearId={academicYearId}
          classSectionId={classSectionId ?? null}
          discounts={discounts}
          feeTypes={feeTypes}
        />
      </SectionCard>
    </div>
  );
}

/* ---------- Admission bill preview (mirrors backend FeeInvoiceService.generateOnEnrollment) ---------- */

interface LinePreview {
  key: number;
  feeTypeId: number;
  feeTypeName: string;
  frequency: 'ONE_TIME' | 'MONTHLY' | 'PER_TERM' | string | null;
  gross: number;
  discount: number;
  net: number;
}

function AdmissionBillPreview({
  academicYearId,
  classSectionId,
  discounts,
  feeTypes,
}: {
  academicYearId: number | null;
  classSectionId: number | null;
  discounts: DiscountRow[];
  feeTypes: { id: number; name: string }[];
}) {
  const { data: structures, isFetching } = useQuery({
    queryKey: ['fee', 'structures', academicYearId ?? 'none', classSectionId ?? 'none'],
    queryFn: () => feeApi.listStructures(academicYearId!, classSectionId!),
    enabled: academicYearId != null && classSectionId != null,
    staleTime: FIVE_MIN,
  });

  if (classSectionId == null) {
    return (
      <Alert
        type="info"
        showIcon
        message="Pick a class · section to preview the admission bill."
        style={{ marginBottom: 12 }}
      />
    );
  }

  if (isFetching) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        Loading fee structure…
      </Typography.Text>
    );
  }

  if (!structures || structures.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            No fee structure defined. Configure in Fee → Structures.
          </Typography.Text>
        }
        style={{ marginBlock: 4 }}
      />
    );
  }

  // Backend skips PER_TERM on enrollment — split them out for honest preview.
  const billed: LinePreview[] = [];
  const deferred: LinePreview[] = [];

  for (const s of structures) {
    const gross = Number(s.amount) || 0;
    const matching = discounts.filter(
      (d) => d.feeTypeId === s.feeTypeId && d.value != null && Number(d.value) > 0,
    );
    const rawDiscount = matching.reduce((sum, d) => {
      const v = Number(d.value);
      return sum + (d.percentage ? (gross * v) / 100 : v);
    }, 0);
    const discount = Math.min(rawDiscount, gross);
    const line: LinePreview = {
      key: s.id,
      feeTypeId: s.feeTypeId,
      feeTypeName: s.feeTypeName ?? `Fee #${s.feeTypeId}`,
      frequency: s.frequency,
      gross,
      discount,
      net: Math.max(gross - discount, 0),
    };
    if (s.frequency === 'PER_TERM') deferred.push(line);
    else billed.push(line);
  }

  const grossTotal = billed.reduce((sum, l) => sum + l.gross, 0);
  const discountTotal = billed.reduce((sum, l) => sum + l.discount, 0);
  const netTotal = billed.reduce((sum, l) => sum + l.net, 0);

  // Surface discounts that don't match any billed fee type (helps the user spot mistakes).
  const billedFeeTypeIds = new Set(structures.map((s) => s.feeTypeId));
  const orphanDiscounts = discounts.filter(
    (d) =>
      d.feeTypeId != null &&
      d.value != null &&
      Number(d.value) > 0 &&
      !billedFeeTypeIds.has(d.feeTypeId),
  );

  return (
    <div style={{ paddingBottom: 12 }}>
      <Space orientation="vertical" size={10} style={{ width: '100%' }}>
        {billed.map((line) => (
          <BillLine key={line.key} line={line} />
        ))}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingTop: 8,
            borderTop: '1px dashed var(--ant-color-border-secondary)',
            fontSize: 13,
          }}
        >
          <Typography.Text type="secondary">Subtotal</Typography.Text>
          <Typography.Text>Rs {grossTotal.toLocaleString('en-PK')}</Typography.Text>
        </div>
        {discountTotal > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <Typography.Text type="success">Discounts</Typography.Text>
            <Typography.Text type="success">
              − Rs {discountTotal.toLocaleString('en-PK')}
            </Typography.Text>
          </div>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingTop: 6,
            borderTop: '1px solid var(--ant-color-border-secondary)',
          }}
        >
          <Typography.Text strong style={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 12 }}>
            Billed at admission
          </Typography.Text>
          <Typography.Text strong style={{ fontSize: 15 }}>
            Rs {netTotal.toLocaleString('en-PK')}
          </Typography.Text>
        </div>
      </Space>

      {deferred.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Term fees · billed separately
          </Typography.Text>
          <Space orientation="vertical" size={4} style={{ width: '100%', marginTop: 6 }}>
            {deferred.map((line) => (
              <div key={line.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <Typography.Text type="secondary">{line.feeTypeName}</Typography.Text>
                <Typography.Text type="secondary">Rs {line.gross.toLocaleString('en-PK')}</Typography.Text>
              </div>
            ))}
          </Space>
        </div>
      )}

      {orphanDiscounts.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message={
            <Typography.Text style={{ fontSize: 12 }}>
              {orphanDiscounts.length === 1 ? 'A discount targets' : `${orphanDiscounts.length} discounts target`}{' '}
              a fee type with no structure for this class · section
              {orphanDiscounts[0].feeTypeId != null
                ? ` (${feeTypes.find((f) => f.id === orphanDiscounts[0].feeTypeId)?.name ?? '—'}${orphanDiscounts.length > 1 ? '…' : ''})`
                : ''}
              {' '}— it won't apply on save.
            </Typography.Text>
          }
        />
      )}

      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 10 }}>
        One-time fees bill in full. Monthly fees bill for the enrollment-date month; subsequent months come from the
        monthly run.
      </Typography.Text>
    </div>
  );
}

function BillLine({ line }: { line: LinePreview }) {
  const tagColor =
    line.frequency === 'ONE_TIME' ? 'gold' : line.frequency === 'MONTHLY' ? 'blue' : 'default';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
      <Space size={8} style={{ minWidth: 0 }}>
        <Tag color={tagColor} variant="filled" style={{ margin: 0 }}>
          {frequencyLabel(line.frequency)}
        </Tag>
        <Typography.Text style={{ fontSize: 13 }}>{line.feeTypeName}</Typography.Text>
      </Space>
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {line.discount > 0 ? (
          <>
            <Typography.Text delete type="secondary" style={{ fontSize: 11, display: 'block' }}>
              Rs {line.gross.toLocaleString('en-PK')}
            </Typography.Text>
            <Typography.Text strong style={{ fontSize: 13 }}>
              Rs {line.net.toLocaleString('en-PK')}
            </Typography.Text>
          </>
        ) : (
          <Typography.Text strong style={{ fontSize: 13 }}>
            Rs {line.gross.toLocaleString('en-PK')}
          </Typography.Text>
        )}
      </div>
    </div>
  );
}

function frequencyLabel(f: string | null) {
  switch (f) {
    case 'MONTHLY':
      return 'monthly';
    case 'ONE_TIME':
      return 'one-time';
    case 'PER_TERM':
      return 'per term';
    default:
      return '—';
  }
}
